import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'
import { withI18next } from 'lib/i18n/withI18next'
import BackdropModal from 'components/portal/backdrop-modal'
import { BUTTON_HTML_TYPE, BUTTON_TYPES } from 'constants/button'
import Icon, { ICONS, ICON_SIZE } from 'components/common/icon'
import ReviewApiHandler from 'lib/api/review'
import VirtualTryOnApiHandler from 'lib/api/virtual-try-on'
import env from 'config/env'
import Typo from 'constants/typography'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import { v4 as uuid } from 'uuid'
import Button from 'components/common/button'
import { hasLocizeTranslation } from 'lib/utils/locize'
import { segmentVirtualTryOnPDPClicked } from 'components/pdp/product-virtual-try-on/pdp-virtual-try-on-segment/pdpVirtualTryOnSegment'
import { compressImage } from 'lib/utils/images/compression'
import styles from './virtual-try-on-modal.scss'
import VirtualTryOnOnboarding from './virtual-try-on-onboarding/VirtualTryOnOnboarding'
import VirtualTryOnGeneratedImage from './virtual-try-on-generated-image/VirtualTryOnGeneratedImage'

export const GENERATED_IMAGE_STATUS_ENUM = {
  PROCESSING: 'processing',
  SUCCESS: 'success',
  FAILED: 'failed',
}

const VirtualTryOnModal = ({
  t,
  isPhone,
  productId,
  product,
  triggerButton,
  isVirtualTryOn,
}) => {
  // states
  const [isOpenModal, setIsOpenModal] = useState(isVirtualTryOn)
  const [secondsLeft, setSecondsLeft] = useState(null)
  const [uploadedImage, setUploadedImage] = useState(null)
  const [cdnImage, setCdnImage] = useState(null)
  const [generatedImage, setGeneratedImage] = useState(null)
  const [displayImage, setDisplayImage] = useState(null)
  const [isFailedToGenerateImage, setIsFailedToGenerateImage] = useState(false)

  const uuId = uuid()
  const productCategory = product?.category
  const clothingPhoto = product?.metadata?.full_cover_image_path ?? ''
  const id_category = product?.category?.id_category
  const isShowLoadingScreen = Boolean(!generatedImage?.image_url)
  const description = product?.name
  const loadingTitle = isFailedToGenerateImage
    ? hasLocizeTranslation(
        t,
        'GENERATE_IMAGE_ERROR',
        `We couldn't generate your look.`,
      )
    : hasLocizeTranslation(t, 'TRYING_IT_ON', 'Trying it on...')
  const loadingSubTitle = isFailedToGenerateImage
    ? hasLocizeTranslation(t, 'PLEASE_TRY_AGAIN', 'Please try again.')
    : hasLocizeTranslation(
        t,
        'VIRTUAL_LEAVING_PAGE_WARNING',
        `Hold on! If you leave now, you'll lose your progress.`,
      )
  const loadingCount = secondsLeft ? `${secondsLeft} seconds` : ''

  // events
  const handleResetStates = () => {
    setSecondsLeft(null)
    setGeneratedImage(null)
    setUploadedImage(null)
    setDisplayImage(null)
    setCdnImage(null)
    setIsFailedToGenerateImage(false)
  }

  const handleUploadImagesToS3 = async (imageFile) => {
    const imagePath = `img/virtual-try-on/${productId}${imageFile?.lastModified}${uuId}`

    try {
      const presignedUrl = await ReviewApiHandler.getPresignedUrl({
        body: {
          path: imagePath,
        },
      })

      const bodyFormData = new FormData()
      Object.keys(presignedUrl.presigned_url.fields).forEach((key) => {
        bodyFormData.append(key, presignedUrl.presigned_url.fields[key])
      })
      bodyFormData.append('file', imageFile)

      await ReviewApiHandler.s3UploadImageFile(presignedUrl.presigned_url.url, {
        body: bodyFormData,
      })
    } catch (error) {
      // eslint-disable-next-line
      console.error(error)
    }

    return imagePath
  }

  const handleGenerateImage = async (model_photo, clothing_photo) => {
    setSecondsLeft(30)

    try {
      const result = await VirtualTryOnApiHandler.virtualTryOnImageGenerator({
        body: {
          model_photo,
          clothing_photo,
          description,
          id_category,
        },
      })

      if (result) {
        setGeneratedImage(result)
        setDisplayImage(result?.image_url)
        setSecondsLeft(0)
      }
    } catch (error) {
      // eslint-disable-next-line
      console.error(error)
      setIsFailedToGenerateImage(true)
      setSecondsLeft(0)
    }
  }

  const handleUploadImage = async (imageFile) => {
    const compressedFile = await compressImage(imageFile, 0.7, 2000)

    // create blob image url from image file to display the image locally
    const blobImageFile = {
      url: URL.createObjectURL(compressedFile),
      lastModified: compressedFile?.lastModified,
      isLocal: true,
    }
    setUploadedImage(blobImageFile)
    setDisplayImage(blobImageFile?.url)

    // upload the image file to S3
    const imagePath = await handleUploadImagesToS3(compressedFile)

    // after uploading it retreive the uploaded cdn image from s3 and generate the image
    const cdnImageFromS3 = `${env.IMG_HOST}${imagePath}`
    setCdnImage(cdnImageFromS3)
    await handleGenerateImage(cdnImageFromS3, clothingPhoto)
  }

  const handleRetry = async () => {
    setIsFailedToGenerateImage(false)
    await handleGenerateImage(cdnImage, clothingPhoto)
  }

  // interval pooling every 10 seconds
  useEffect(() => {
    const myInterval = setInterval(async () => {
      if (secondsLeft > 0) {
        setSecondsLeft((prevSecondsLeft) => prevSecondsLeft - 1)
      }
    }, 1000)

    return () => {
      clearInterval(myInterval)
    }
  }, [secondsLeft])

  const handleClickTrigger = () => {
    setIsOpenModal(true)
    segmentVirtualTryOnPDPClicked({
      id_product: productId,
      site_category_name: productCategory?.link_rewrite,
      id_site_category: productCategory?.id_category,
    })
  }

  return (
    <React.Fragment>
      <style jsx>{styles}</style>
      {triggerButton &&
        React.cloneElement(triggerButton, {
          onClick: (e) => {
            e.stopPropagation()
            handleClickTrigger()
            triggerButton.props.onClick?.(e)
          },
          onKeyDown: (e) => {
            e.stopPropagation()
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleClickTrigger()
            }
          },
        })}
      <BackdropModal
        freezeBody
        zIndex={2000}
        isOpen={isOpenModal}
        onRouteChange={() => {
          setIsOpenModal(false)
          handleResetStates()
        }}
      >
        <div className="virtual-try-on-modal">
          {uploadedImage ? (
            <VirtualTryOnGeneratedImage
              loadingInfo={
                <React.Fragment>
                  <span className={Typo.subtitle2}>{loadingTitle}</span>
                  <span className={Typo.body2}>{loadingSubTitle}</span>
                  {isFailedToGenerateImage ? (
                    <Button
                      style={{ width: 'fit-content' }}
                      type={BUTTON_TYPES.primary}
                      onClick={handleRetry}
                    >
                      {hasLocizeTranslation(t, 'Retry', `Retry`)}
                    </Button>
                  ) : (
                    <span className={Typo.caption}>{loadingCount}</span>
                  )}
                </React.Fragment>
              }
              isShowLoadingScreen={isShowLoadingScreen}
              productId={productId}
              imageUrl={displayImage}
              closeButton={
                <button
                  data-cy="virtual-try-on-modal__close-ctn"
                  className="virtual-try-on-modal__close-ctn"
                  type={BUTTON_HTML_TYPE.button}
                  onClick={() => {
                    setIsOpenModal(false)
                    handleResetStates()
                  }}
                >
                  <Icon
                    alt="close dialog"
                    src={ICONS.close}
                    size={ICON_SIZE.medium}
                  />
                </button>
              }
            />
          ) : (
            <VirtualTryOnOnboarding
              isPhone={isPhone}
              productId={productId}
              onUploadImage={handleUploadImage}
              closeButton={
                <button
                  data-cy="virtual-try-on-modal__close-ctn"
                  className="virtual-try-on-modal__close-ctn"
                  type={BUTTON_HTML_TYPE.button}
                  onClick={() => {
                    setIsOpenModal(false)
                    handleResetStates()
                  }}
                >
                  <Icon
                    alt="close dialog"
                    src={ICONS.close}
                    size={ICON_SIZE.medium}
                  />
                </button>
              }
            />
          )}
        </div>
      </BackdropModal>
    </React.Fragment>
  )
}

VirtualTryOnModal.defaultProps = {
  isPhone: false,
  product: undefined,
  isVirtualTryOn: false,
}

VirtualTryOnModal.propTypes = {
  isPhone: PropTypes.bool,
  t: PropTypes.func.isRequired,
  product: PropTypes.shape(),
  triggerButton: PropTypes.node.isRequired,
  productId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  isVirtualTryOn: PropTypes.bool,
}

export default compose(
  connect((state) => ({
    isPhone: state.device.isPhone,
    product: state.product.payload,
  })),
  withI18next(),
)(VirtualTryOnModal)
